import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    fetchTeams,
    createTeam,
    updateTeamServices,
    deleteTeam,
    getTeamById,
} from '../../api/taxi/taxiApi';

const TEAMS_QUERY_KEY = ['teams'];

/**
 * Normaliza la respuesta del backend
 */
const normalizeTeamResponse = (response) => response?.data ?? response;

export const useTeams = () => {
    const queryClient = useQueryClient();

    // Lista de equipos
    const teamsQuery = useQuery({
        queryKey: TEAMS_QUERY_KEY,
        queryFn: fetchTeams,
        staleTime: 60 * 1000,
    });

    // Crear equipo
    const createMutation = useMutation({
        mutationFn: createTeam,
        onSuccess: (response) => {
            const team = normalizeTeamResponse(response);
            queryClient.invalidateQueries({ queryKey: TEAMS_QUERY_KEY });
            if (team?.equConVehId) {
                queryClient.setQueryData(
                    ['team', Number(team.equConVehId)],
                    team
                );
            }
        },
    });

    // Actualizar servicios del equipo
    const updateServicesMutation = useMutation({
        mutationFn: ({ equConVehId, data }) =>
            updateTeamServices(equConVehId, data),
        onSuccess: (response, variables) => {
            const team = normalizeTeamResponse(response);
            const teamId = Number(variables.equConVehId);
            queryClient.invalidateQueries({ queryKey: TEAMS_QUERY_KEY });
            queryClient.invalidateQueries({ queryKey: ['team', teamId] });
            if (team?.equConVehId) {
                queryClient.setQueryData(
                    ['team', Number(team.equConVehId)],
                    team
                );
            }
        },
    });

    // Eliminar (desactivar) equipo
    const deleteMutation = useMutation({
        mutationFn: deleteTeam,
        onSuccess: (_response, teamId) => {
            const id = Number(teamId);
            queryClient.invalidateQueries({ queryKey: TEAMS_QUERY_KEY });
            queryClient.removeQueries({ queryKey: ['team', id] });
        },
    });

    return {
        teams: teamsQuery.data ?? [],
        error: teamsQuery.error,
        isLoading: teamsQuery.isLoading,
        isFetching: teamsQuery.isFetching,
        refetch: teamsQuery.refetch,

        createMutation,
        updateServicesMutation,
        deleteMutation,
    };
};

/**
 * Hook para obtener detalle de un equipo específico
 */
export const useTeamDetail = (equConVehId) => {
    const teamId = Number(equConVehId);
    const isValid = Number.isInteger(teamId) && teamId > 0;

    const teamQuery = useQuery({
        queryKey: ['team', teamId],
        queryFn: () => getTeamById(teamId),
        enabled: isValid,
        staleTime: 60 * 1000,
    });

    return {
        team: teamQuery.data,
        isLoading: teamQuery.isLoading,
        isFetching: teamQuery.isFetching,
        error: teamQuery.error,
        refetch: teamQuery.refetch,
    };
};